import { createContext, useEffect, useState, useContext } from "react";
import ApiUser from "../apiUser";
import { ClientContext } from "./clientProvider";

export const FaturadorContext = createContext();

export const FaturadorProvider = ({ children }) => {
  const { idCliente } = useContext(ClientContext);
  const [patrimonios, setPatrimonios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [secretarias, setSecretarias] = useState([]);
  const [currSec, setCurrSec] = useState([]);
  const [filterDates, setFilterDates] = useState({ dataInicio: null, dataFim: null, checkUpdate: false });
  const [grupos, setGrupos] = useState([]);

  let idCounter = 1;

  useEffect(() => {
    if (idCliente) {
      fetchAndProcessEquipamentos(idCliente);
    }
  }, [idCliente]);

  useEffect(() => {
    if (filterDates.dataInicio && filterDates.dataFim) {
      fetchAndProcessEquipamentos(idCliente);
    }
  }, [filterDates]);

  const fetchAndProcessEquipamentos = async (id) => {
    setLoading(true);
    try {
      const pats = await fetchPats(id);
      const filteredPats = filterAndFormatPats(pats);
      setPatrimonios(filteredPats);
      setTotal(calculateTotalByGrupoValor(filteredPats));
    } catch (error) {
      console.error('Erro ao buscar e processar equipamentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSecs = async () => {
    setLoading(true);
    try {
      const resp = await ApiUser.post("/secretarias/buscar");
      return resp?.data;
    } catch (error) {
      console.error('Erro ao buscar secretarias:', error);
    } finally {
      setLoading(false);
    }
  }


  useEffect(() => {
    if (patrimonios.length > 0) {
      const sec = secretarias?.find(s => s.erpID === idCliente)
      if (sec) {
        let idCounter = 1;
        const updatedSec = sec.secretarias.map((sec) => ({
          ...sec,
          id: idCounter++
        }));
        setCurrSec(updatedSec);
      } else {
        setCurrSec([]);
      }
    }
  }, [patrimonios])

  const fetchPats = async (id) => {
    const params = {
      dataInicio: filterDates.dataInicio || null,
      dataFim: filterDates.dataFim || null,
      checkUpdate: filterDates.checkUpdate || false
    };

    const [respPatrimonios, respNfs, respEquipsByClient, respItens] = await Promise.all([
      ApiUser.get(`/patrimonios/${id}`, { params }),
      ApiUser.get(`/patrimonios/nfs/${id}`, { params }),
      ApiUser.post(`/equipamentos/equipamentosByClient`, { id }),
      ApiUser.post("/itens/dados", { id_cliente: id })
    ]);
    let pats = [...respPatrimonios.data, ...respNfs.data];
    const grupoFs = await normalizeGrupos(respEquipsByClient, respItens);
    if (grupoFs.length > 0) {
      for (let i = 0; i < pats.length; i++) {
        const pat = pats[i];
        const [grupoNome, grupoId, grupoValor] = getPatGroup(pat, grupoFs);
        pat.grupo_nome = grupoNome;
        pat.grupo_id = grupoId;
        pat.grupo_valor = grupoValor;
      }
    }
    return pats;
  };

  const normalizeGrupos = async (respEquipsByClient, respItens) => {
    let newGrupos = [];
    if (respEquipsByClient.data.success) {
      const { data } = respEquipsByClient.data;
      const groupIdNomesEValor = data.map(gEquip => {
        const modelosIdNomes = gEquip.modelos?.[0]
        let grupoValor = ''
        if (respItens.data.success) {
          let { dados } = respItens.data;
          for (let i = 0; i < dados.length; i++) {
            const item = dados[i];
            if (item.tipo == "Equipamento" && item.grupo == gEquip.grupoId) {
              grupoValor = item.valor_unitario;
            }
          }
        }
        return { ...modelosIdNomes, id: gEquip.grupoId, nome: gEquip.grupoNome, valor: grupoValor }
      }).filter(m => m !== undefined)
      newGrupos = groupIdNomesEValor;
    }
    setGrupos(newGrupos);
    return newGrupos;
  }


  function getPatGroup(pat, grouPequips) {
    for (let i = 0; i < grouPequips.length; i++) {
      const group = grouPequips[i];
      let modelsIds = group.modeloId.split(",");
      if (modelsIds.some(m => m == pat.modelo_id)) {
        return [group.nome, group.id, group.valor];
      }
    }
    return [null, null, null];
  }
  const filterAndFormatPats = (pats) => {
    const seen = new Set();
    return pats.filter(e => {
      if (!seen.has(e.modelo_id)) {
        return true;
      }
      return false;
    }).map(formatPatrimonio);
  };

  const formatPatrimonio = (e) => ({
    ...e,
    id: idCounter++,
    marca_id: e.marca_id || '',
    modelo_id: e.modelo_id || '',
    marca_nome: e.marca_nome || '',
    modelo_nome: e.modelo_nome || '',
    netiqueta: e.netiqueta || '',
    nmac: e.nmac || '',
    nome_do_anexo: e.nome_do_anexo || '',
    nserie: e.nserie || '',
    pat_nome: e.pat_nome || '',
    produto: e.produto || '',
    setor: e.setor || ''
  });

  const toBrl = (valor) => 'R$ ' + parseFloat(valor).toFixed(2).toString().replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  const calculateTotalByGrupoValor = (pats) => {
    return pats.reduce((acc, cur) => {
      const unitStr = cur?.grupo_valor;
      let unit = 0;
      if (typeof unitStr === 'number') {
        unit = unitStr;
      } else if (typeof unitStr === 'string' && unitStr.trim() !== '') {
        // Verifica se é formato brasileiro (com vírgula) ou decimal normal (com ponto)
        const cleanStr = unitStr.trim();
        if (cleanStr.includes(',')) {
          // Formato brasileiro: remove pontos (separadores de milhar) e troca vírgula por ponto
          const parsed = parseFloat(cleanStr.replace(/\./g, '').replace(',', '.'));
          unit = isNaN(parsed) ? 0 : parsed;
        } else {
          // Formato decimal normal ou apenas números
          const parsed = parseFloat(cleanStr);
          unit = isNaN(parsed) ? 0 : parsed;
        }
      }
      return acc + unit;
    }, 0);
  };

  const updatePat = (pat) => {
    let newPatrimonios = patrimonios.map((patrimonio) => {
      // Atualiza todos os patrimônios com o mesmo modelo_id
      if (patrimonio.modelo_id === pat.modelo_id && patrimonio.marca_id === pat.marca_id) {
        return { ...patrimonio, grupo_valor: pat.grupo_valor };
      }
      return patrimonio;
    });

    setPatrimonios(newPatrimonios);
    const updatedTotalVal = calculateTotalByGrupoValor(newPatrimonios);
    setTotal(updatedTotalVal);
  };

  const saveValues = async (modelId, marcaId, valor) => {
    const body = {
      modelo: modelId,
      valor: valor,
      marca: marcaId,
      cliente: idCliente,
    };
    setLoading(true);
    try {
      await ApiUser.post('/valores/salvar', body);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log(err.response?.data || err.message);
    }
  };

  return (
    <FaturadorContext.Provider
      value={{
        patrimonios,
        loading, setLoading,
        total,
        updatePat,
        toBrl,
        saveValues,
        calculateTotalByGrupoValor,
        clienteSelecionado, setClienteSelecionado,
        secretarias,
        currSec, setCurrSec,
        filterDates, setFilterDates,
        grupos,
        fetchSecs,
      }}>
      {children}
    </FaturadorContext.Provider>
  );
};
